import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
    ArrowLeft, MagnifyingGlass, ArrowsClockwise,
    Lightning, Eye, EyeSlash, Trophy, CaretDown, CaretRight,
    DownloadSimple, Share, XLogo, WhatsappLogo,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import MinimalShell from "../components/MinimalShell";
import HelpPanel from "../components/HelpPanel";
import WCStrategyNav from "../components/WCStrategyNav";
import {
    getMatchdayCall, generateMatchdayCall, getMatchdayLeaderboard, api,
} from "../lib/api";
import { ACTIVE_ROUNDS } from "../lib/wcRounds";
import { useReputation, RepCounter, FloatingReps, MilestoneBanner } from "../components/RepFx";

// ---- helpers ----------------------------------------------------------------

const DECISIONS = ["BUY", "KEEP", "SELL", "AVOID"];

/**
 * Returns a cleaned-up object retaining only keys whose numeric form is in
 * `validIds`, or the SAME reference if nothing changed (so React effects
 * can bail out without re-rendering).
 */
function pruneStale(obj, validIds) {
    const keep = {};
    let changed = false;
    for (const [k, v] of Object.entries(obj || {})) {
        if (validIds.has(Number(k))) keep[k] = v;
        else changed = true;
    }
    return changed ? keep : obj;
}
const DECISION_COLORS = {
    BUY: { bg: "bg-fa-mint", text: "text-black", border: "border-fa-mint" },
    KEEP: { bg: "bg-fa-yellow", text: "text-black", border: "border-fa-yellow" },
    SELL: { bg: "bg-fa-amber", text: "text-black", border: "border-fa-amber" },
    AVOID: { bg: "bg-red-500/80", text: "text-white", border: "border-red-500/60" },
};

const TIER_BADGES = {
    HOT: { label: "🔥 HOT", border: "border-red-400/70", text: "text-red-300" },
    POPULAR: { label: "POPULAR", border: "border-fa-yellow/70", text: "text-fa-yellow" },
    NICHE: { label: "NICHE", border: "border-fa-mint/60", text: "text-fa-mint" },
    DEEP: { label: "DEEP", border: "border-white/15", text: "text-white/40" },
};

function fmtKick(iso) {
    if (!iso) return "TBD";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

function storageKey(md) {
    return `wc_md_call_md${md}`;
}

function loadDraft(md) {
    try {
        const raw = localStorage.getItem(storageKey(md));
        return raw ? JSON.parse(raw) : { decisions: {}, confidences: {} };
    } catch {
        return { decisions: {}, confidences: {} };
    }
}

function saveDraft(md, draft) {
    try {
        localStorage.setItem(storageKey(md), JSON.stringify(draft));
    } catch {
        /* ignore quota */
    }
}

// ---- Player row -------------------------------------------------------------

function PlayerRow({ player, decision, onDecision, scoreInfo, crowdStats }) {
    const sel = player.selected || {};
    const tier = TIER_BADGES[sel.tier] || TIER_BADGES.DEFAULT || TIER_BADGES.DEEP;
    const crowdTotal = crowdStats?.total_decisions || 0;
    const crowdPct = (k) => crowdTotal > 0 ? Math.round(100 * (crowdStats?.counts?.[k] || 0) / crowdTotal) : 0;

    return (
        <div
            className={`flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-2.5 border-b border-white/5 hover:bg-white/[0.02] ${
                decision ? "bg-white/[0.015]" : ""
            }`}
            data-testid={`md-player-row-${player.playerId}`}
        >
            {/* Identity + meta */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    {player.score_rank && (
                        <span className="font-mono text-[9px] tabular-nums text-fa-yellow w-4 shrink-0">#{player.score_rank}</span>
                    )}
                    <span className="font-display font-black text-[14px] sm:text-[13px] truncate">
                        {player.webName}
                    </span>
                    <span className="font-mono text-[9px] text-white/30 tabular-nums">
                        #{player.shirtNumber || "—"}
                    </span>
                    <span className={`font-mono text-[8px] uppercase tracking-widest px-1 border ${tier.border} ${tier.text}`}>
                        {tier.label}
                    </span>
                    {player.isDarkHorse && (
                        <span
                            className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-violet/60 text-fa-violet"
                            title={`Dark horse nation: ${player.nation} — historically capable of upset.`}
                            data-testid={`md-dark-horse-${player.playerId}`}
                        >
                            DH
                        </span>
                    )}
                    {player.wcOneToWatch && (
                        <span
                            className="font-mono text-[8px] uppercase tracking-widest px-1 border border-fa-yellow/60 text-fa-yellow"
                            title="FIFA One-to-Watch: official breakout pick."
                            data-testid={`md-otw-${player.playerId}`}
                        >
                            OTW
                        </span>
                    )}
                    {/* Score badge — inline on mobile, separate column on sm+ */}
                    {scoreInfo && (
                        <span
                            className={`sm:hidden font-mono font-bold text-[10px] tabular-nums px-1.5 py-0.5 border ml-auto ${
                                scoreInfo.points >= 8 ? "bg-fa-mint text-black border-fa-mint"
                                : scoreInfo.points >= 4 ? "border-fa-mint/60 text-fa-mint"
                                : scoreInfo.points >= 1 ? "border-white/15 text-white/70"
                                : scoreInfo.points < 0 ? "border-red-500/40 text-red-400"
                                : "border-white/10 text-white/40"
                            }`}
                        >
                            {scoreInfo.points > 0 ? `+${scoreInfo.points}` : scoreInfo.points}
                        </span>
                    )}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                    {player.position} · {player.nation}
                    {sel.selected_pct != null && (
                        <> · sel {sel.selected_pct.toFixed(1)}%</>
                    )}
                    {player.wcPrice != null && (
                        <> · <span className="text-fa-yellow">£{player.wcPrice.toFixed(1)}m</span></>
                    )}
                    {player.wcXpts != null && (
                        <> · <span className="text-fa-mint">{player.wcXpts.toFixed(2)} xPts</span></>
                    )}
                    {player.modelRecommendation && (
                        <> · <span className="text-white/50">model: {player.modelRecommendation}</span></>
                    )}
                </div>
            </div>

            {/* Score badge — desktop only (mobile shows inline above) */}
            {scoreInfo && (
                <div
                    className="hidden sm:flex items-center gap-1 mr-1"
                    title={scoreInfo.breakdown?.map((b) => `${b.label} (${b.value > 0 ? "+" : ""}${b.value})`).join(" · ") || ""}
                    data-testid={`md-pts-${player.playerId}`}
                >
                    <span className={`font-mono font-bold text-[11px] tabular-nums px-2 py-0.5 border ${
                        scoreInfo.points >= 8 ? "bg-fa-mint text-black border-fa-mint"
                        : scoreInfo.points >= 4 ? "border-fa-mint/60 text-fa-mint"
                        : scoreInfo.points >= 1 ? "border-white/15 text-white/70"
                        : scoreInfo.points < 0 ? "border-red-500/40 text-red-400"
                        : "border-white/10 text-white/40"
                    }`}>
                        {scoreInfo.points > 0 ? `+${scoreInfo.points}` : scoreInfo.points}
                    </span>
                </div>
            )}

            {/* Decision buttons — 4-col grid on mobile (big touch targets),
                inline on sm+ */}
            <div className="grid grid-cols-4 gap-1.5 sm:flex sm:items-center sm:gap-1 sm:w-auto">
                {DECISIONS.map((d) => {
                    const active = decision === d;
                    const color = DECISION_COLORS[d];
                    const pct = crowdStats ? crowdPct(d) : null;
                    return (
                        <button
                            key={d}
                            type="button"
                            onClick={(e) => onDecision(active ? null : d, e.currentTarget.getBoundingClientRect())}
                            data-testid={`md-decision-${player.playerId}-${d}`}
                            className={`relative font-mono text-[11px] sm:text-[10px] uppercase tracking-widest py-2.5 sm:py-1 px-2 border transition-all active:scale-95 overflow-hidden ${
                                active
                                    ? `${color.bg} ${color.text} ${color.border} shadow-[0_0_0_2px_rgba(232,248,32,0.15)]`
                                    : "border-white/10 text-white/40 hover:text-white hover:border-white/30"
                            }`}
                        >
                            {/* Crowd-vote fill bar — sits behind the label */}
                            {crowdStats && !active && (
                                <div
                                    className={`absolute inset-0 ${
                                        d === "BUY" ? "bg-fa-mint/15"
                                        : d === "KEEP" ? "bg-fa-yellow/15"
                                        : d === "SELL" ? "bg-fa-amber/15"
                                        : "bg-red-500/15"
                                    }`}
                                    style={{ width: `${pct}%`, pointerEvents: "none" }}
                                    aria-hidden
                                />
                            )}
                            <span className="relative z-10 flex flex-col items-center gap-0">
                                <span>{d}</span>
                                {crowdStats && (
                                    <span className="text-[8px] font-mono opacity-70 leading-none mt-0.5">{pct}%</span>
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---- Flat view (all players, no match grouping) ----------------------------

function FlatView({ players, decisions, confidences, onDecision, onConfidence,
                   searchQuery, positionFilter, hideUndecided, minXpts,
                   pointsByPlayerId, sortBy, crowdStatsByPid, showCrowd }) {
    const passFilters = (p) => {
        if (positionFilter && p.position !== positionFilter) return false;
        if (hideUndecided && !decisions[p.playerId]) return false;
        // Only apply xPts cutoff when slider is above 0 AND the player actually
        // has xPts data. Players without FIFA Fantasy data (e.g. production
        // before xlsx import) must still render — fall back to score_rank.
        if (minXpts != null && minXpts > 0 && p.wcXpts != null && p.wcXpts < minXpts) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!p.webName?.toLowerCase().includes(q)
                && !p.nation?.toLowerCase().includes(q)) return false;
        }
        return true;
    };
    // Sort by xPts desc (real WC fantasy projection), undecided last;
    // players without xPts fall back to score_rank ordering. When actual
    // post-match `pointsByPlayerId` is provided AND `sortBy === "points"`,
    // sort by realized points instead.
    const sorted = [...players].sort((a, b) => {
        if (sortBy === "points" && pointsByPlayerId) {
            const ap = pointsByPlayerId[String(a.playerId)]?.points;
            const bp = pointsByPlayerId[String(b.playerId)]?.points;
            // Players without scores go last; otherwise points desc.
            if (ap == null && bp == null) {/* fallthrough */}
            else if (ap == null) return 1;
            else if (bp == null) return -1;
            else if (bp !== ap) return bp - ap;
        }
        const ax = a.wcXpts ?? -1;
        const bx = b.wcXpts ?? -1;
        if (bx !== ax) return bx - ax;
        return (a.score_rank ?? 99) - (b.score_rank ?? 99);
    });
    const visible = sorted.filter(passFilters);
    return (
        <div className="border border-white/10 bg-[#0a0a0a]" data-testid="md-flat-view">
            <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-fa-mint bg-white/[0.02]">
                All interesting players · {visible.length} shown · sorted by {sortBy === "points" ? "actual MD pts" : "xPts"}
            </div>
            {visible.length === 0 ? (
                <div className="p-4 font-mono text-[12px] text-white/40">
                    No players match current filters.
                </div>
            ) : visible.map((p) => (
                <PlayerRow
                    key={p.playerId}
                    player={p}
                    decision={decisions[p.playerId]}
                    onDecision={(d, rect) => onDecision(p.playerId, d, rect)}
                    scoreInfo={pointsByPlayerId?.[String(p.playerId)] || null}
                    crowdStats={showCrowd ? (crowdStatsByPid?.[String(p.playerId)] || null) : null}
                />
            ))}
        </div>
    );
}


// ---- Leaderboard ------------------------------------------------------------

function LeaderboardPanel({ matchday }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = () => {
        setLoading(true);
        getMatchdayLeaderboard(matchday, 50)
            .then(setData)
            .catch(() => toast.error("Leaderboard unavailable"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!open || data) return;
        // Defer the load to a microtask so the strict
        // react-hooks/set-state-in-effect rule doesn't fire.
        Promise.resolve().then(() => load());
    }, [open, matchday]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="border-2 border-fa-yellow/30 bg-[#0d0d0d]" data-testid="md-leaderboard-panel">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="md-leaderboard-toggle"
                className="w-full flex items-center justify-between gap-3 p-3 hover:bg-white/[0.02]"
            >
                <div className="flex items-center gap-2">
                    {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    <Trophy size={14} weight="fill" className="text-fa-yellow" />
                    <span className="font-display font-black text-sm uppercase tracking-tight">
                        MD{matchday} Leaderboard
                    </span>
                    {data && (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                            · {data.entry_count} submissions
                            {data.status === "open" && " · awaiting matches"}
                            {data.status === "partially_resolved" && " · partial"}
                            {data.status === "evaluated" && " · final ✓"}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); load(); }}
                    data-testid="md-leaderboard-refresh"
                    className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-white/15 text-white/40 hover:text-white"
                >
                    Refresh
                </button>
            </button>
            {open && (
                <div className="border-t border-white/10">
                    {loading && !data ? (
                        <div className="p-4 font-mono text-[12px] text-white/40">Loading…</div>
                    ) : !data || data.entry_count === 0 ? (
                        <div className="p-4 font-mono text-[12px] text-white/40">No submissions yet. Be the first!</div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            <div className="grid grid-cols-[2rem_1fr_6rem_5rem_6rem] gap-2 px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-white/30 bg-white/[0.02]">
                                <div>#</div>
                                <div>User</div>
                                <div className="text-right">Score</div>
                                <div className="text-right">Picked</div>
                                <div className="text-right">B/K/S/A</div>
                            </div>
                            {data.entries.map((row, i) => (
                                <div
                                    key={row.user_id}
                                    data-testid={`md-leaderboard-row-${i + 1}`}
                                    className="grid grid-cols-[2rem_1fr_6rem_5rem_6rem] gap-2 px-3 py-2 items-center hover:bg-white/[0.02]"
                                >
                                    <div className={`font-display font-black text-sm tabular-nums ${
                                        i === 0 ? "text-fa-yellow" : i < 3 ? "text-fa-mint" : "text-white/40"
                                    }`}>
                                        {i + 1}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display font-black text-[13px] truncate">
                                            {row.display_name}
                                            {row.is_guest && (
                                                <span className="text-white/30 font-mono text-[9px] ml-1">guest</span>
                                            )}
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/30">
                                            {row.resolved ? `${row.graded_count} graded` : "submitted"}
                                        </div>
                                    </div>
                                    <div className={`font-mono text-[12px] tabular-nums text-right ${
                                        row.total_score === null || row.total_score === undefined
                                            ? "text-white/30"
                                            : row.total_score > 0
                                                ? "text-fa-mint"
                                                : row.total_score < 0
                                                    ? "text-red-400"
                                                    : "text-white/60"
                                    }`}>
                                        {row.total_score === null || row.total_score === undefined
                                            ? "—" : `${row.total_score > 0 ? "+" : ""}${row.total_score}`}
                                    </div>
                                    <div className="font-mono text-[10px] tabular-nums text-right text-white/50">
                                        {row.decided_count}
                                    </div>
                                    <div className="font-mono text-[9px] tabular-nums text-right text-white/40">
                                        {row.counts?.BUY || 0}·{row.counts?.KEEP || 0}·{row.counts?.SELL || 0}·{row.counts?.AVOID || 0}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ---- Page wrapper -----------------------------------------------------------

export default function WorldCupMatchday({ minimal = false }) {
    const { md } = useParams();
    const matchday = parseInt(md, 10) || 1;
    const navigate = useNavigate();
    const [challenge, setChallenge] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [decisions, setDecisions] = useState({});
    const [confidences, setConfidences] = useState({});
    const [search, setSearch] = useState("");
    const [positionFilter, setPositionFilter] = useState(null);
    const [hideUndecided, setHideUndecided] = useState(false);
    const [minXpts, setMinXpts] = useState(0);
    const [pointsByPid, setPointsByPid] = useState(null);
    const [sortBy, setSortBy] = useState("xpts"); // xpts | points
    const [showCrowd, setShowCrowd] = useState(true);
    const [crowdStatsByPid, setCrowdStatsByPid] = useState(null);

    // Load challenge + draft. We funnel state changes via a single setter
    // queued through Promise.resolve() so the strict
    // `react-hooks/set-state-in-effect` rule (newer plugin) doesn't fire.
    useEffect(() => {
        let cancelled = false;
        const draft = loadDraft(matchday);
        Promise.resolve().then(() => {
            if (cancelled) return;
            setDecisions(draft.decisions || {});
            setConfidences(draft.confidences || {});
            setLoading(true);
        });
        getMatchdayCall(matchday)
            .then((d) => { if (!cancelled) setChallenge(d); })
            .catch((e) => {
                if (cancelled) return;
                if (e.response?.status !== 404) {
                    toast.error("Failed to load matchday call");
                }
                setChallenge(null);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [matchday]);

    // Prune stale decisions whenever the challenge changes — picks from a
    // previous publication may still linger in localStorage. Without this,
    // the user can end up with 21/20 DECIDED and submit fails with
    // "Invalid input — unknown player ids: [...]". Wrapping in a microtask
    // because the React 19 `set-state-in-effect` rule is over-eager here.
    useEffect(() => {
        if (!challenge?.players) return;
        const validIds = new Set(challenge.players.map((p) => p.playerId));
        Promise.resolve().then(() => {
            const cleanedD = pruneStale(decisions, validIds);
            const cleanedC = pruneStale(confidences, validIds);
            if (cleanedD !== decisions) setDecisions(cleanedD);
            if (cleanedC !== confidences) setConfidences(cleanedC);
        });
    }, [challenge]);

    // Fetch actual scored points for this MD (if any matches are finished).
    useEffect(() => {
        let cancelled = false;
        api.get(`/worldcup/player-round-scores/${matchday}`)
            .then((r) => { if (!cancelled) setPointsByPid(r.data?.by_player_id || {}); })
            .catch(() => { if (!cancelled) setPointsByPid(null); });
        return () => { cancelled = true; };
    }, [matchday]);

    // Lazy-fetch crowd vote stats when the user enables the toggle. Refresh
    // on every toggle ON so newcomers see fresh data after every submission.
    useEffect(() => {
        if (!showCrowd) return;
        let cancelled = false;
        api.get(`/worldcup/matchday-call/${matchday}/stats`)
            .then((r) => {
                if (cancelled) return;
                const byPid = {};
                for (const p of (r.data?.players || [])) byPid[String(p.playerId)] = p;
                setCrowdStatsByPid({ total_attempts: r.data?.total_attempts || 0, byPid });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [showCrowd, matchday]);

    // Persist draft on change
    useEffect(() => {
        if (challenge) {
            saveDraft(matchday, { decisions, confidences });
        }
    }, [decisions, confidences, matchday, challenge]);

    const handleGenerate = async (regenerate = false) => {
        setGenerating(true);
        try {
            const d = await generateMatchdayCall(matchday, { regenerate });
            setChallenge(d);
            toast.success(`${d.round_label || `Round ${matchday}`} ready · ${d.playerCount} players from ${new Set(d.players.map((p) => p.team_id)).size} teams`);
        } catch {
            toast.error("Generate failed");
        } finally {
            setGenerating(false);
        }
    };

    // Reputation gamification — per-MD persistent
    const repApi = useReputation(matchday, challenge?.players);

    const handleDecision = (pid, decision, anchorRect) => {
        const player = (challenge?.players || []).find((p) => p.playerId === pid);
        const decidedCountNow = Object.keys(decisions).length;
        setDecisions((prev) => {
            const next = { ...prev };
            if (decision === null) {
                delete next[pid];
            } else {
                next[pid] = decision;
            }
            return next;
        });
        if (decision === null) {
            repApi.revoke(pid);
            return;
        }
        if (repApi.granted(pid)) return; // already credited this player
        repApi.grant({
            playerId: pid,
            decision,
            modelMatch: player?.modelRecommendation === decision,
            anchorRect,
            decidedCount: decidedCountNow,
            totalPlayers: challenge?.playerCount || 0,
        });
    };

    const handleConfidence = (pid, conf) => {
        setConfidences((prev) => ({ ...prev, [pid]: conf }));
    };

    // ---- Submit ------------------------------------------------------------

    const [submitting, setSubmitting] = useState(false);
    const [submittedAttempt, setSubmittedAttempt] = useState(null);
    const [displayName, setDisplayName] = useState(() => {
        try { return localStorage.getItem("wc_md_call_display_name") || ""; } catch { return ""; }
    });

    // Restore prior submission
    useEffect(() => {
        if (!challenge) return;
        const guestId = localStorage.getItem("wc_md_call_guest_user_id");
        if (!guestId) return;
        api.get(`/worldcup/matchday-call/${matchday}/my-attempt`, {
            params: { user_id: guestId },
        })
            .then((r) => {
                if (r.data?.submitted) {
                    setSubmittedAttempt(r.data.attempt);
                }
            })
            .catch(() => { /* ignore */ });
    }, [challenge, matchday]);

    const handleSubmit = async () => {
        const decidedIds = Object.keys(decisions);
        if (decidedIds.length === 0) {
            toast.error("Decide on at least one player first");
            return;
        }
        const name = (displayName || "").trim();
        if (!name) {
            toast.error("Enter a display name to submit");
            return;
        }
        try {
            localStorage.setItem("wc_md_call_display_name", name);
        } catch { /* ignore */ }
        setSubmitting(true);
        try {
            const r = await api.post(`/worldcup/matchday-call/${matchday}/submit`, {
                decisions,
                confidences,
                display_name: name,
            });
            toast.success(`Submitted · ${r.data.decided_count}/${r.data.total_players} (BUY ${r.data.counts.BUY} · KEEP ${r.data.counts.KEEP} · SELL ${r.data.counts.SELL} · AVOID ${r.data.counts.AVOID})`);
            if (r.data.user?.user_id) {
                try {
                    localStorage.setItem("wc_md_call_guest_user_id", r.data.user.user_id);
                } catch { /* ignore */ }
            }
            // Refetch the persisted attempt for the confirmation panel
            const attempt = await api.get(`/worldcup/matchday-call/${matchday}/my-attempt`, {
                params: { user_id: r.data.user?.user_id },
            });
            if (attempt.data?.submitted) {
                setSubmittedAttempt(attempt.data.attempt);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    const decidedCount = Object.keys(decisions).length;
    const totalPlayers = challenge?.playerCount || 0;

    // Format MD1 deadline (first match kickoff) for the help banner
    const firstKick = challenge?.matches?.[0]?.utc_date;
    const deadlineLabel = firstKick
        ? new Date(firstKick).toLocaleString(undefined, {
            weekday: "short", day: "numeric", month: "short",
            hour: "2-digit", minute: "2-digit",
        })
        : null;

    const Shell = minimal ? MinimalShell : AppShell;
    const shellProps = minimal
        ? {
            shareUrl: typeof window !== "undefined" ? window.location.href : "",
            shareLabel: `World Cup '26 Round Call · ${challenge?.round_label || `Round ${matchday}`}`,
        }
        : {};

    return (
        <Shell {...shellProps}>
            <section className="space-y-5">
                {!minimal && (
                    <Link to="/worldcup" className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5">
                        <ArrowLeft size={11} weight="bold" /> Back to WC Hub
                    </Link>
                )}

                {/* Shared nav — only in full app view */}
                {!minimal && <WCStrategyNav activeKey="round-call" />}

                {/* Public-friendly help panel — shown in both views; defaults
                    collapsed for power users in the full app, expanded for
                    first-time public visitors. */}
                <HelpPanel deadline={deadlineLabel} />

                <header className="border-2 border-fa-yellow/40 bg-[#0d0d0d] p-4 sm:p-6" data-testid="wc-matchday-header">
                    <div className="flex items-start justify-between gap-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                            <Lightning size={11} weight="fill" />
                            <span className="hidden sm:inline">WC Round Call · </span>{challenge?.round_label || `Round ${matchday}`}
                        </div>
                        <RepCounter value={repApi.rep} compact />
                    </div>
                    <h1 className="font-display font-black text-xl sm:text-2xl md:text-3xl uppercase tracking-tight mt-2 leading-tight">
                        Buy · Keep · Sell · Avoid<br />
                        <span className="text-base sm:text-2xl md:text-3xl">{challenge?.playerCount || 20} hot picks for {challenge?.round_label || `Round ${matchday}`}</span>
                    </h1>
                    <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed hidden sm:block">
                        One shared challenge — the top 20 most fantasy-relevant players across every match in this round, capped at 2 per team for diversity. Powered by <span className="text-fa-mint">WC Fantasy xPts</span> + ownership %. Always includes <span className="text-fa-violet">dark-horse stars</span> next to the favourites.
                    </p>
                    <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed sm:hidden">
                        Tap BUY/KEEP/SELL/AVOID. Earn <span className="text-fa-yellow">+5 REP</span> per pick. Bonus for matching the model and hitting milestones.
                    </p>
                    {challenge && challenge.players && challenge.players.length > 0 && (
                        <div className="mt-3 flex items-center gap-3 flex-wrap font-mono text-[10px] uppercase tracking-widest" data-testid="md-tier-mix">
                            <span className="text-white/40">Mix:</span>
                            <span className="text-fa-cream border border-fa-cream/40 px-1.5 py-0.5">
                                {challenge.players.filter(p => p.isTopTier).length} favourites
                            </span>
                            <span className="text-fa-violet border border-fa-violet/40 px-1.5 py-0.5">
                                {challenge.players.filter(p => p.isDarkHorse).length} dark horses
                            </span>
                            <span className="text-white/50 border border-white/15 px-1.5 py-0.5">
                                {challenge.players.filter(p => !p.isTopTier && !p.isDarkHorse).length} mid-tier
                            </span>
                            {challenge.players.some(p => p.wcOneToWatch) && (
                                <span className="text-fa-yellow border border-fa-yellow/40 px-1.5 py-0.5">
                                    {challenge.players.filter(p => p.wcOneToWatch).length} FIFA OTW
                                </span>
                            )}
                            {!minimal && (
                                <button
                                    type="button"
                                    onClick={() => handleGenerate(true)}
                                    disabled={generating}
                                    className="ml-auto border border-fa-yellow/40 text-fa-yellow hover:bg-fa-yellow/10 px-2 py-1 disabled:opacity-40 flex items-center gap-1"
                                    data-testid="md-regenerate-btn"
                                    title="Force regenerate this round's Round Call using the latest FIFA Fantasy data and scoring engine."
                                >
                                    <ArrowsClockwise size={10} weight="bold" />
                                    {generating ? "Regenerating…" : "Regenerate"}
                                </button>
                            )}
                        </div>
                    )}
                </header>

                {/* Round switcher — hides past rounds via ACTIVE_FROM_MD config */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40 shrink-0">Round</span>
                    {ACTIVE_ROUNDS.map(({ n, label }) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => navigate(minimal ? `/play/round-call/${n}` : `/worldcup/matchday/${n}`)}
                            data-testid={`md-switch-${n}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border shrink-0 snap-start ${
                                matchday === n
                                    ? "bg-fa-yellow text-black border-fa-yellow"
                                    : "border-white/15 text-white/60 hover:text-white"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="font-mono text-[12px] text-white/40">Loading matchday call…</div>
                ) : !challenge ? (
                    <div className="border border-white/10 bg-[#0d0d0d] p-6 text-center space-y-3">
                        <div className="font-mono text-[12px] text-white/60">
                            {minimal
                                ? `Round ${matchday} isn't published yet — check back closer to kick-off.`
                                : `Matchday ${matchday} challenge not generated yet.`}
                        </div>
                        {!minimal && (
                            <button
                                type="button"
                                onClick={handleGenerate}
                                disabled={generating}
                                data-testid="md-generate-btn"
                                className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 bg-fa-yellow text-black border border-fa-yellow disabled:opacity-50"
                            >
                                {generating ? "Generating…" : `Generate MD${matchday} →`}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <LeaderboardPanel matchday={matchday} />

                        {/* Toolbar */}
                        <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-center gap-3 flex-wrap" data-testid="md-toolbar">
                            <div className="flex-1 min-w-[180px] flex items-center gap-2 border border-white/10 px-2">
                                <MagnifyingGlass size={11} weight="bold" className="text-white/30" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search player or nation"
                                    data-testid="md-search"
                                    className="bg-transparent border-0 outline-none font-mono text-[11px] text-white/80 placeholder-white/30 py-1.5 w-full"
                                />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mr-1">Pos</span>
                                {["GKP", "DEF", "MID", "FWD"].map((p) => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setPositionFilter(positionFilter === p ? null : p)}
                                        data-testid={`md-pos-${p}`}
                                        className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${
                                            positionFilter === p
                                                ? "bg-fa-cream text-black border-fa-cream"
                                                : "border-white/10 text-white/40 hover:text-white"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => setHideUndecided((v) => !v)}
                                data-testid="md-hide-undecided"
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1 ${
                                    hideUndecided
                                        ? "bg-fa-mint text-black border-fa-mint"
                                        : "border-white/10 text-white/40 hover:text-white"
                                }`}
                            >
                                {hideUndecided ? <Eye size={10} /> : <EyeSlash size={10} />}
                                {hideUndecided ? "Decided only" : "Show all"}
                            </button>
                        </div>

                        <div className="flex items-center gap-2 font-mono text-[10px] text-white/50" data-testid="md-xpts-filter">
                            <span className="uppercase tracking-widest text-white/40">Min xPts</span>
                            <input
                                type="range"
                                min={0} max={7} step={0.5}
                                value={minXpts}
                                onChange={(e) => setMinXpts(parseFloat(e.target.value))}
                                className="w-32 accent-fa-mint"
                            />
                            <span className="tabular-nums text-fa-mint w-10 text-right">{minXpts.toFixed(1)}</span>
                        </div>

                        {/* Progress */}
                        <div className="flex items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-widest text-white/50 flex-wrap" data-testid="md-progress">
                            <span><strong className="text-fa-yellow tabular-nums">{decidedCount}</strong>/{totalPlayers} decided</span>
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setShowCrowd((v) => !v)}
                                    data-testid="md-show-crowd-toggle"
                                    title="Show how many players have voted BUY/KEEP/SELL/AVOID on each player so far"
                                    className={`px-2 py-0.5 border ${
                                        showCrowd
                                            ? "bg-fa-yellow text-black border-fa-yellow"
                                            : "border-white/15 text-white/60 hover:text-white"
                                    }`}
                                >
                                    {showCrowd ? "👥 Crowd: ON" : "👥 Crowd: OFF"}
                                </button>
                                {crowdStatsByPid?.total_attempts > 0 && showCrowd && (
                                    <span className="text-fa-mint" data-testid="md-crowd-total">
                                        {crowdStatsByPid.total_attempts} voted
                                    </span>
                                )}
                                {pointsByPid && Object.keys(pointsByPid).length > 0 && (
                                    <>
                                        <span className="text-fa-mint" data-testid="md-graded-badge">
                                            ✓ {challenge.players.filter((p) => pointsByPid[String(p.playerId)]).length}/{challenge.playerCount} graded
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setSortBy(sortBy === "points" ? "xpts" : "points")}
                                            data-testid="md-sort-toggle"
                                            className={`px-2 py-0.5 border ${
                                                sortBy === "points"
                                                    ? "bg-fa-mint text-black border-fa-mint"
                                                    : "border-white/15 text-white/60 hover:text-white"
                                            }`}
                                        >
                                            Sort: {sortBy === "points" ? "actual pts" : "xPts"}
                                        </button>
                                    </>
                                )}
                                <span className="hidden sm:inline">{challenge.matchCount} matches</span>
                            </div>
                        </div>
                        <MilestoneBanner
                            rep={repApi.rep}
                            decidedCount={decidedCount}
                            totalPlayers={totalPlayers}
                        />

                        {/* All players (flat top-N from across the round) */}
                        <FlatView
                            players={challenge.players || []}
                            decisions={decisions}
                            confidences={confidences}
                            onDecision={handleDecision}
                            onConfidence={handleConfidence}
                            searchQuery={search}
                            positionFilter={positionFilter}
                            hideUndecided={hideUndecided}
                            minXpts={minXpts}
                            pointsByPlayerId={pointsByPid}
                            sortBy={sortBy}
                            crowdStatsByPid={crowdStatsByPid?.byPid}
                            showCrowd={showCrowd}
                        />

                        {/* Submit footer */}
                        <div
                            className="border-2 border-fa-mint/40 bg-[#0d0d0d] p-3 sm:p-4 sticky bottom-3 mb-12 sm:mb-0 flex items-center gap-3 flex-wrap"
                            data-testid="md-submit-footer"
                        >
                            <div className="flex-1 min-w-[180px]">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    Display name {submittedAttempt ? "· (locked after submit)" : ""}
                                </div>
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="Your handle (required)"
                                    data-testid="md-display-name"
                                    disabled={!!submittedAttempt}
                                    className="bg-transparent border-b border-white/15 outline-none font-display font-black text-base text-white py-1 w-full disabled:opacity-60"
                                />
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-2">
                                {decidedCount}/{totalPlayers} decided
                                <RepCounter value={repApi.rep} compact />
                            </div>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || decidedCount === 0}
                                data-testid="md-submit-btn"
                                className="bg-fa-mint text-black font-display font-black uppercase tracking-wide text-sm px-5 py-3 sm:py-2.5 hover:bg-fa-mint/90 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                            >
                                {submitting ? "Submitting…" : submittedAttempt ? "Update submission →" : "Submit attempt →"}
                            </button>
                        </div>

                        {submittedAttempt && <PostSubmitReview matchday={matchday} attempt={submittedAttempt} players={challenge?.players || []} decisions={decisions} />}
                    </>
                )}
            </section>
            <FloatingReps floats={repApi.floats} />
        </Shell>
    );
}

// ---- Post-submit "your results" panel ---------------------------------------

function PostSubmitReview({ matchday, attempt, players, decisions }) {
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const shareCardRef = useRef(null);
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const [s, l] = await Promise.all([
                    api.get(`/worldcup/matchday-call/${matchday}/stats`).then((r) => r.data),
                    api.get(`/worldcup/matchday-call/${matchday}/leaderboard?limit=10`).then((r) => r.data),
                ]);
                if (!cancelled) { setStats(s); setLeaderboard(l); }
            } catch { /* silent */ }
        }
        load();
        return () => { cancelled = true; };
    }, [matchday, attempt?.attempt_id]);

    const statsById = (stats?.players || []).reduce((acc, p) => { acc[p.playerId] = p; return acc; }, {});
    const myRank = (leaderboard?.entries || []).findIndex((e) => e.display_name === attempt.display_name) + 1;
    const counts = attempt.counts || {};
    const c = (k) => counts[k] || 0;
    const decidedPlayers = players.filter((p) => decisions[p.playerId]);
    const agreementCount = decidedPlayers.reduce((acc, p) => {
        const ps = statsById[p.playerId];
        return ps && ps.consensus === decisions[p.playerId] ? acc + 1 : acc;
    }, 0);
    const agreementPct = decidedPlayers.length > 0
        ? Math.round(100 * agreementCount / decidedPlayers.length)
        : 0;

    const handleDownload = async (modeShare = false) => {
        if (!shareCardRef.current) return;
        setDownloading(true);
        try {
            const dataUrl = await toPng(shareCardRef.current, {
                cacheBust: true,
                backgroundColor: "#0a0a0a",
                pixelRatio: 2,
                style: { borderRadius: "0" },
            });
            const fileName = `round-call-md${matchday}-${(attempt.display_name || "me").replace(/[^a-z0-9-]/gi, "_")}.png`;
            if (modeShare && navigator.share && navigator.canShare) {
                try {
                    const blob = await (await fetch(dataUrl)).blob();
                    const file = new File([blob], fileName, { type: "image/png" });
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            title: `Round Call · MD${matchday} · ${attempt.display_name}`,
                            text: `My WC '26 Round ${matchday} picks vs the crowd — agreement ${agreementPct}% with ${stats?.total_attempts || "—"} players.`,
                            files: [file],
                        });
                        return;
                    }
                } catch {
                    /* fall through to download */
                }
            }
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("PNG downloaded");
        } catch (e) {
            toast.error("Couldn't generate PNG — try again");
            console.error(e);
        } finally {
            setDownloading(false);
        }
    };

    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

    return (
        <div className="space-y-3" data-testid="md-post-submit-review">
            {/* Download/Share PNG controls */}
            {stats && (
                <div
                    className="flex items-center justify-between gap-3 flex-wrap border border-fa-yellow/30 bg-[#0d0d0d] p-3"
                    data-testid="md-share-controls"
                >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 leading-relaxed">
                        <span className="text-fa-yellow">Brag rights →</span> save your picks card and share it with friends.
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => handleDownload(false)}
                            disabled={downloading}
                            data-testid="md-download-png-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 bg-fa-yellow text-black border border-fa-yellow hover:bg-fa-yellow/90 disabled:opacity-50 flex items-center gap-1.5"
                        >
                            <DownloadSimple size={11} weight="bold" />
                            {downloading ? "Rendering…" : "Download PNG"}
                        </button>
                        {(() => {
                            const url = typeof window !== "undefined" ? window.location.href : "";
                            const tweetText = `My WC '26 Round ${matchday} picks: ${c("BUY")} BUY · ${c("KEEP")} KEEP · ${c("SELL")} SELL · ${c("AVOID")} AVOID. Agreement with the crowd: ${agreementPct}%. Play yours ↓`;
                            const xUrl = `https://x.com/intent/post?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`;
                            const waUrl = `https://wa.me/?text=${encodeURIComponent(tweetText)}%20${encodeURIComponent(url)}`;
                            return (
                                <>
                                    <a
                                        href={xUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="md-share-x-btn"
                                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-white/40 text-white hover:bg-white/10 flex items-center gap-1.5"
                                        title="Share on X (Twitter)"
                                    >
                                        <XLogo size={11} weight="bold" />
                                        X
                                    </a>
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="md-share-whatsapp-btn"
                                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/10 flex items-center gap-1.5"
                                        title="Share on WhatsApp"
                                    >
                                        <WhatsappLogo size={11} weight="fill" />
                                        WhatsApp
                                    </a>
                                </>
                            );
                        })()}
                        {canNativeShare && (
                            <button
                                type="button"
                                onClick={() => handleDownload(true)}
                                disabled={downloading}
                                data-testid="md-share-png-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint/10 disabled:opacity-50 flex items-center gap-1.5"
                            >
                                <Share size={11} weight="fill" />
                                Share PNG
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Shareable card — captured into PNG */}
            <div ref={shareCardRef} className="space-y-3 bg-[#0a0a0a] p-3" data-testid="md-share-card">
                {/* Header banner */}
                <div className="border-2 border-fa-mint/50 bg-[linear-gradient(135deg,#06251a,#0d0d0d)] p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-1.5">
                                ✓ Submitted · Round Call · MD{matchday}
                            </div>
                            <h3 className="font-display font-black text-xl uppercase tracking-tight mt-1">
                                {attempt.display_name}&apos;s round {matchday} picks are locked in
                            </h3>
                        </div>
                        <div className="font-mono text-[11px] text-white/60 flex items-center gap-3 flex-wrap">
                            <span className="text-fa-mint">BUY {c("BUY")}</span>
                            <span className="text-fa-yellow">KEEP {c("KEEP")}</span>
                            <span className="text-[#F08080]">SELL {c("SELL")}</span>
                            <span className="text-white/50">AVOID {c("AVOID")}</span>
                        </div>
                    </div>
                    <p className="font-mono text-[11px] text-white/60 mt-2">
                        Final score arrives once all MD{matchday} matches finish.
                        {stats && (
                            <> You&apos;re player <span className="text-fa-yellow">#{stats.total_attempts}</span> · agreement with the crowd: <span className="text-fa-mint">{agreementPct}%</span> ({agreementCount}/{decidedPlayers.length})</>
                        )}
                    </p>
                </div>

                {/* Crowd consensus per pick */}
                {stats && (
                    <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="md-crowd-consensus">
                        <h4 className="font-display font-black text-sm uppercase tracking-tight mb-2">You vs the crowd</h4>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
                            How the {stats.total_attempts} submitted picks line up with yours
                        </p>
                        <div className="space-y-1.5">
                            {players.map((p) => {
                                const my = decisions[p.playerId];
                                const ps = statsById[p.playerId];
                                if (!ps) return null;
                                const total = ps.total_decisions || 0;
                                const pct = (k) => total > 0 ? Math.round(100 * (ps.counts?.[k] || 0) / total) : 0;
                                const cons = ps.consensus;
                                const agreesWithCrowd = my && my === cons;
                                return (
                                    <div key={p.playerId} className="flex items-center gap-2 p-2 border-l-2 hover:bg-white/[0.02]"
                                        style={{ borderLeftColor: agreesWithCrowd ? "rgba(96,224,168,0.7)" : my ? "rgba(240,128,128,0.5)" : "rgba(255,255,255,0.05)" }}
                                        data-testid={`md-consensus-${p.playerId}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-black text-[12px] truncate">{p.webName}</div>
                                            <div className="font-mono text-[9px] uppercase tracking-widest text-white/30">{p.position} · {p.nation}</div>
                                        </div>
                                        <div className="flex items-center gap-1 text-[9px] font-mono tabular-nums">
                                            {["BUY", "KEEP", "SELL", "AVOID"].map((k) => {
                                                const isMine = my === k;
                                                const isCons = cons === k && total > 0;
                                                return (
                                                    <div key={k} className="text-center">
                                                        <div className={`px-1.5 py-0.5 border ${
                                                            isMine ? "border-fa-mint text-fa-mint bg-fa-mint/10"
                                                            : isCons ? "border-fa-yellow/40 text-fa-yellow/70"
                                                            : "border-white/10 text-white/30"
                                                        }`}>{k[0]}</div>
                                                        <div className="text-white/30 mt-0.5">{pct(k)}%</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-3 flex items-center gap-3 flex-wrap">
                            <span><span className="inline-block w-3 h-0.5 bg-fa-mint mr-1"></span>You match crowd</span>
                            <span><span className="inline-block w-3 h-0.5 bg-[#F08080]/60 mr-1"></span>You disagree</span>
                            <span><span className="inline-block w-3 h-0.5 bg-fa-yellow/50 mr-1"></span>Crowd consensus</span>
                        </p>
                    </div>
                )}

                {/* Brand watermark — only meaningful in the exported PNG */}
                <div
                    className="flex items-center justify-between gap-3 px-3 py-2 border-t border-fa-yellow/20 bg-[#0a0a0a]"
                    data-testid="md-share-card-watermark"
                >
                    <div className="flex items-center gap-2">
                        <span className="bg-fa-yellow text-black p-1 leading-none">
                            <Trophy size={12} weight="fill" />
                        </span>
                        <div className="leading-tight">
                            <div className="font-display font-black text-[11px] uppercase tracking-tight">Fantasy Academy</div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">Round Call · World Cup &apos;26</div>
                        </div>
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow text-right">
                        Play your round →<br/>
                        <span className="text-white/50">{(typeof window !== "undefined" ? window.location.host : "fantasyacademy.cz")}/play/round-call/{matchday}</span>
                    </div>
                </div>
            </div>

            {/* Top 10 leaderboard preview */}
            {leaderboard && (leaderboard.entries || []).length > 0 && (
                <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="md-mini-leaderboard">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-display font-black text-sm uppercase tracking-tight">Top {Math.min(10, leaderboard.entries.length)} submissions</h4>
                        {myRank > 0 && (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                                You: #{myRank}
                            </span>
                        )}
                    </div>
                    <div className="space-y-1">
                        {leaderboard.entries.slice(0, 10).map((e, i) => {
                            const isMe = e.display_name === attempt.display_name;
                            return (
                                <div
                                    key={`${e.display_name}-${i}`}
                                    className={`flex items-center gap-2 py-1 px-2 ${isMe ? "bg-fa-yellow/5 border-l-2 border-fa-yellow" : ""}`}
                                >
                                    <span className="font-mono text-[10px] tabular-nums text-white/40 w-5">#{i + 1}</span>
                                    <span className="font-display font-black text-[12px] flex-1 truncate">
                                        {e.display_name}{isMe && <span className="text-fa-yellow"> (you)</span>}
                                    </span>
                                    <span className="font-mono text-[10px] text-white/50">{e.decided_count}/20</span>
                                    {e.resolved && <span className="font-mono text-[10px] text-fa-mint tabular-nums">{e.total_score}</span>}
                                </div>
                            );
                        })}
                    </div>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-2">
                        Updates live as more people submit · final scores computed after all MD{matchday} matches finish
                    </p>
                </div>
            )}
        </div>
    );
}
